import { ClipboardDocumentListIcon, PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import { PropsWithChildren, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Button } from '@/components/core/Button';
import { Loader } from '@/components/core/Loader';
import { PanelHeader } from '@/components/core/Panel';
import {
    ItemsList,
    type ItemsListComponentProps,
    type ItemsListSelectionControls,
} from '@/components/itemsList/itemsList';
import {
    DataModelDependentPage,
    DeploymentInProgress,
    NoDataModelEmptyState,
} from '@/components/shared';
import { DialogHost } from '@/dialogs/DialogHost';
import { ItemReviewsDialog } from '@/dialogs/item-reviews/ItemReviewsDialog';
import { useDataModel } from '@/hooks/useDataModel';
import { type OpenItemEditorParams, useDialogHost } from '@/hooks/useDialogHost';
import { useOrganization } from '@/hooks/useOrganization';
import { useTranslation } from '@/hooks/useTranslation';
import { getInjector, getRootScope } from '@/services/angularjs-bridge';
import type { ItemsQuery } from '@/types/ItemsQuery';

import {
    BrowsePageRouteProps,
    browseQueriesMatch,
    categoryFromLocation,
    discardBrowseSearch,
    editorParamsForRoute,
    primaryKeyFromRow,
    publishBrowseUrl,
    readBrowseRoute,
    rememberBrowseSearch,
    restoreBrowseLocation,
    routeFromProps,
    routeOpensEditor,
} from './browseRoute';
import { BrowseSectionHeader } from './components/BrowseSectionHeader';
import { SearchFilterBar } from './components/SearchFilterBar';
import {
    type BrowseListSelection,
    EMPTY_BROWSE_SELECTION,
    useBrowseDeleteAction,
} from './delete-items';
import { useBrowseQuery } from './hooks/useBrowseQuery';
import { useCopyCompareActions } from './hooks/useCopyCompareActions';
import { useCreateNewItem } from './hooks/useCreateNewItem';
import { useOpenCompareFromUrl } from './hooks/useOpenCompareFromUrl';
import { itemRestrictions, useExactItemCount } from './toolbar-actions';
import { resolveCounter } from './toolbar-actions/useBrowseToolbarActions';
import { ItemsListMeta } from './types';

const noop = () => undefined;

function searchParamsFrom(value: object): Record<string, unknown> {
    const params: Record<string, unknown> = {};
    Object.assign(params, value);
    return params;
}

const selectionKeys = (items: Array<{ primaryKey__?: string }>) =>
    items.map((item) => item.primaryKey__ ?? '').join('\0');

const sameBrowseSelection = (current: BrowseListSelection, next: BrowseListSelection) =>
    current.allItemsSelected === next.allItemsSelected
    && selectionKeys(current.selectedItems) === selectionKeys(next.selectedItems)
    && selectionKeys(current.loadedItems) === selectionKeys(next.loadedItems)
    && current.watchlistPrimaryKeys.join('\0') === next.watchlistPrimaryKeys.join('\0');

/**
 * Angular selection broadcasts stay no-ops on this page. The list reports
 * its own selection so Delete Item(s) can render above the grid.
 */
const DEFAULT_ITEMS_LIST_PROPS: ItemsListComponentProps = {
    selectAllItems: noop,
    clearSelectedItems: noop,
    handleSelectedItems: noop,
    isCatalogTable: false,
    loadWatchlistItems: noop,
};

interface BrowseHitListProps {
    routeProps: BrowsePageRouteProps;
    children?: React.ReactNode;
}

interface OpenItemReview {
    primaryKey: string;
    publicationDestination?: string;
    reviewer?: string;
}

const BrowseHitList = ({ routeProps, children }: BrowseHitListProps) => {
    const { t } = useTranslation();
    const dialogHost = useDialogHost();
    const openedFromRoute = useRef(false);
    useOpenCompareFromUrl();
    const {
        query,
        setQuery,
        activeFilters,
        setActiveFilter,
        removeFilter,
        applyBulkSearch,
    } = useBrowseQuery();
    const [itemsMeta, setItemsMeta] = useState<ItemsListMeta | null>(null);
    const queryRef = useRef(query);
    queryRef.current = query;
    const queryWhenEditorOpened = useRef<ItemsQuery | null>(null);
    const [itemReview, setItemReview] = useState<OpenItemReview | null>(null);
    const [listSelection, setListSelection] = useState<BrowseListSelection>(EMPTY_BROWSE_SELECTION);
    const pauseEditingRef = useRef<(() => void) | null>(null);
    const selectionControlsRef = useRef<ItemsListSelectionControls | null>(null);
    // Read by the floating toolbar mount. Only shows or hides the toolbar.
    const [bulkActionsAlwaysVisible, setBulkActionsAlwaysVisible] = useState(true);
    const [routeVersion, setRouteVersion] = useState(0);

    useEffect(() => () => {
        discardBrowseSearch();
    }, []);

    useEffect(() => {
        try {
            const root = getRootScope(getInjector());
            if (typeof root?.$on !== 'function') {
                return undefined;
            }
            const bump = () => setRouteVersion((version) => version + 1);
            const offs = ['$routeUpdate', '$routeChangeSuccess'].map((event) => root.$on?.(event, bump));
            return () => offs.forEach((off) => off?.());
        } catch {
            return undefined;
        }
    }, []);

    const routeSelectionId = useMemo(() => {
        // `$routeParams` is mutated in place. `routeVersion` is the re-read signal.
        void routeVersion;
        try {
            const selectionId = getInjector().get('$routeParams')?.selectionId;
            return selectionId == null || selectionId === '' ? undefined : selectionId;
        } catch {
            return undefined;
        }
    }, [routeVersion]);

    const routeCategory = !query.category || query.category === 'NO_CATEGORY'
        ? null
        : query.category;

    const onSelectionChange = useCallback((next: BrowseListSelection) => {
        setListSelection((current) => (
            sameBrowseSelection(current, next) ? current : next
        ));
    }, []);

    const { action: deleteAction, dialog: deleteItemsDialog } = useBrowseDeleteAction({
        ...listSelection,
        itemsQuery: query as Record<string, unknown>,
        routeCategory,
        routeSelectionId,
        pauseEditing: () => pauseEditingRef.current?.(),
    });

    const {
        exactCount,
        counting: countingItems,
        recalculated: countRecalculated,
        refresh: refreshCount,
    } = useExactItemCount(itemRestrictions({
        itemsQuery: query as Record<string, unknown>,
        routeSelectionId,
    }));
    const countedItemsMeta = useMemo(() => (
        itemsMeta && exactCount != null
            ? {
                ...itemsMeta,
                numberFound: exactCount,
                maxNumberFound: Math.max(exactCount, itemsMeta.maxNumberFound),
            }
            : itemsMeta
    ), [exactCount, itemsMeta]);

    const openEditor = useCallback((params: OpenItemEditorParams) => {
        if (!queryWhenEditorOpened.current) {
            queryWhenEditorOpened.current = { ...queryRef.current };
        }
        rememberBrowseSearch();
        dialogHost.openItemEditor({
            ...params,
            category: params.category ?? categoryFromLocation(),
            onSaved: (item) => {
                params.onSaved?.(item);
                const snapshot = queryWhenEditorOpened.current;
                queryWhenEditorOpened.current = null;
                restoreBrowseLocation();
                if (snapshot && !browseQueriesMatch(searchParamsFrom(queryRef.current), searchParamsFrom(snapshot))) {
                    setQuery(() => ({ ...snapshot }));
                }
            },
            onClosed: () => {
                queryWhenEditorOpened.current = null;
                discardBrowseSearch();
            },
        });
    }, [dialogHost, setQuery]);

    const openNewItem = useCallback((params: Partial<OpenItemEditorParams>) => {
        publishBrowseUrl('/edit/new');
        openEditor({
            editorState: undefined,
            forceCloseTask: undefined,
            originalPrimaryKey: '',
            primaryKey: '',
            task: '',
            items: undefined,
            ...params,
        });
    }, [openEditor]);

    const {
        visible: canCreateItem,
        disabled: createNewItemDisabled,
        createNewItem,
    } = useCreateNewItem(openNewItem);

    useEffect(() => {
        if (openedFromRoute.current) {
            return;
        }
        const route = readBrowseRoute(routeFromProps(routeProps));
        if (!routeOpensEditor(route)) {
            return;
        }
        openedFromRoute.current = true;
        openEditor(editorParamsForRoute(route));
    }, [openEditor, routeProps]);

    const openCopy = useCallback((
        copyKey: string,
        options: Partial<Pick<OpenItemEditorParams, 'editorState' | 'items' | 'templateForItemCopy'>> = {},
    ) => {
        publishBrowseUrl(`/copy/${copyKey}`);
        openEditor({
            ...editorParamsForRoute({ routeMode: 'copy', primaryKey: copyKey }),
            ...options,
        });
    }, [openEditor]);

    const showEditor = useCallback<NonNullable<ItemsListComponentProps['showEditor']>>((
        row,
        origPrimaryKey,
        editorState,
        items,
    ) => {
        const copyKey = typeof origPrimaryKey === 'string' && origPrimaryKey.length > 0
            ? origPrimaryKey
            : undefined;
        if (copyKey) {
            openCopy(copyKey, { editorState, items });
            return;
        }
        const primaryKey = primaryKeyFromRow(row);
        if (!primaryKey) {
            return;
        }
        publishBrowseUrl(`/edit/${primaryKey}`);
        openEditor({
            ...editorParamsForRoute({ routeMode: 'edit', primaryKey }),
            editorState,
            items,
        });
    }, [openCopy, openEditor]);

    const { copy, compare } = useCopyCompareActions({
        selectedItems: listSelection.selectedItems,
        allItemsSelected: listSelection.allItemsSelected,
        routeCategory,
        pauseEditing: () => pauseEditingRef.current?.(),
        openCopy: (copyKey, templateForItemCopy) => openCopy(copyKey, { templateForItemCopy }),
    });

    const selectionCounter = resolveCounter({
        ...listSelection,
        routeCategory,
        itemsMeta,
        exactCount: exactCount ?? undefined,
    });

    const showItemReview = useCallback((item: { primaryKey__?: string }) => {
        const primaryKey = item?.primaryKey__;
        if (!primaryKey) {
            return;
        }
        let publicationDestination: string | undefined;
        let reviewer: string | undefined;
        try {
            const $routeParams = getInjector().get('$routeParams');
            publicationDestination = $routeParams.publicationDestination;
            reviewer = $routeParams.reviewer;
        } catch {
            // Storybook and unit tests have no Angular injector.
        }
        setItemReview({ primaryKey, publicationDestination, reviewer });
    }, []);

    const handleKeywordChange = useCallback((keyword: string) => {
        setQuery({ keyword });
    }, [setQuery]);

    // LAX-10131: primary New item action sits in the page header, to the
    // right of the title. Delete Item(s) sits above the list and appears
    // only after the user selects one or more rows.
    const newItemAction = canCreateItem ? (
        <Button
            id="button-create-item"
            variant="default"
            hasIcon
            icon={PlusIcon}
            className="tw-shrink-0"
            disabled={createNewItemDisabled}
            aria-label={t('NEW_ITEM')}
            onClick={createNewItem}
        >
            {t('NEW_ITEM')}
        </Button>
    ) : undefined;

    return (
        <div className="tw-flex tw-flex-col">
            <PanelHeader
                className="tw-shrink-0 !tw-pb-4"
                icon={ClipboardDocumentListIcon}
                title={t('ITEMS')}
                actions={newItemAction}
            />
            <div className="tw-rounded-lg tw-bg-fill-primary tw-p-4">
                {children ?? (
                    <>
                        <SearchFilterBar
                            keyword={query.keyword ?? ''}
                            onKeywordChange={handleKeywordChange}
                            activeFilters={activeFilters}
                            onSetActiveFilter={setActiveFilter}
                            onRemoveFilter={removeFilter}
                            onApplyBulkSearch={applyBulkSearch}
                            itemsMeta={countedItemsMeta}
                            countingItems={countingItems}
                            onRefreshCount={refreshCount}
                            countRecalculated={countRecalculated}
                        />
                        <BrowseSectionHeader
                            allItemsSelected={listSelection.allItemsSelected}
                            totalCount={selectionCounter.totalCount}
                            overCountLimit={!!selectionCounter.overCountLimit}
                            countLimit={selectionCounter.countLimit ?? 0}
                            onSelectAll={() => selectionControlsRef.current?.selectAll()}
                            onClearSelection={() => selectionControlsRef.current?.clearSelection()}
                            bulkActionsAlwaysVisible={bulkActionsAlwaysVisible}
                            onBulkActionsAlwaysVisibleChange={setBulkActionsAlwaysVisible}
                            copy={copy}
                            compare={compare}
                            onRefreshCount={refreshCount}
                            countingItems={countingItems}
                            countRecalculated={countRecalculated}
                        />
                        {deleteAction && (
                            <div className="tw-mb-3 tw-flex">
                                <Button
                                    id="button-delete-items"
                                    variant="destructive"
                                    hasIcon
                                    icon={TrashIcon}
                                    aria-label={t('DELETE_ITEMS')}
                                    onClick={deleteAction.onClick}
                                >
                                    {deleteAction.label}
                                </Button>
                            </div>
                        )}
                        <ItemsList
                            {...DEFAULT_ITEMS_LIST_PROPS}
                            query={query}
                            hideFoundItemsLabel
                            onItemsMetaChange={setItemsMeta}
                            onSelectionChange={onSelectionChange}
                            pauseEditingRef={pauseEditingRef}
                            selectionControlsRef={selectionControlsRef}
                            hideSelectionBanner
                            showEditor={showEditor}
                            deletable
                            showItemReview={showItemReview}
                            createNewItem={canCreateItem ? createNewItem : undefined}
                            createNewItemDisabled={createNewItemDisabled}
                        />
                        {deleteItemsDialog}
                    </>
                )}
            </div>
            {itemReview && (
                <ItemReviewsDialog
                    primaryKey={itemReview.primaryKey}
                    publicationDestination={itemReview.publicationDestination}
                    reviewer={itemReview.reviewer}
                    onClose={() => setItemReview(null)}
                />
            )}
        </div>
    );
};

/**
 * LAX-10128: React shell for `/browse`. Guards mirror `browse.tpl.html`
 * lines 1-22 (loader / first-time deploy / no data model / no browse
 * layout). LAX-10133 adds {@link SearchFilterBar} + {@link useBrowseQuery}.
 *
 * LAX-10137 routes `/browse`, `/edit/*`, and `/copy/*` here when
 * `NEXT_HIT_LIST` is on. LAX-10138 adds `/compare/:first/:second`: the
 * URL opens {@link CompareItemsDialog}, and closing it returns to
 * `/browse`. A primary key opens {@link ItemEditorDialog} through the
 * dialog host. Opening an item from the list uses `$location.displayUrl`
 * so this shell stays mounted. Closing an editor that was opened from
 * `/edit` or `/copy` switches the Angular route back to `/browse`. The
 * Angular items list stays on the same URLs when the flag is off.
 *
 * `children` is a Storybook seam so empty-state stories can avoid
 * mounting the items list.
 */
const BrowsePage = ({
    children,
    routeMode,
    primaryKey,
    section,
    attribute,
}: PropsWithChildren<BrowsePageRouteProps>) => {
    const { organization } = useOrganization();
    const { dataModel } = useDataModel();
    const routeProps = { routeMode, primaryKey, section, attribute };

    const isBlockingDeployment =
        !!organization?.isDataModelDeploying && !organization?.dataModelHash;
    if (isBlockingDeployment) {
        return (
            <DialogHost>
                <DeploymentInProgress />
            </DialogHost>
        );
    }

    if (!dataModel) {
        return (
            <DialogHost>
                <div
                    role="status"
                    aria-label="loading"
                    className="tw-flex tw-items-center tw-justify-center tw-pt-52"
                >
                    <Loader size="lg" />
                </div>
            </DialogHost>
        );
    }

    if (organization?.dataModelHash && !dataModel.hasLayout('browse')) {
        return (
            <DialogHost>
                <NoDataModelEmptyState />
            </DialogHost>
        );
    }

    return (
        <DialogHost>
            <DataModelDependentPage>
                <BrowseHitList routeProps={routeProps}>
                    {children}
                </BrowseHitList>
            </DataModelDependentPage>
        </DialogHost>
    );
};

BrowsePage.displayName = 'BrowsePage';

export { BrowsePage };
